import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CustomPoint, UndisturbedTemperature, UndisturbedTemperaturePlot, UdtViewModel } from '../../wellbore-inputs/models/undisturbed-temperature.model';
import { catchError, map, shareReplay } from 'rxjs/operators';

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class UdtService {

  constructor(private http: HttpClient) { }
  private temperaturePointsCache$: Observable<CustomPoint[]> = null;
  private undisturbedTemperatureCache$: Observable<UndisturbedTemperature> = null;
  private undisturbedTemperaturePlotCache$: Observable<UndisturbedTemperaturePlot> = null;
  private udtViewModelCache$: Observable<UdtViewModel> = null;

  /**
   * Get complete UDT ViewModel for V2 component
   * Backend provides all data, validation rules, and calculation context
   * Frontend only needs to display and bind to forms
   */
  public getData(forceRefresh = false): Observable<UdtViewModel> {
    if (forceRefresh) {
      this.udtViewModelCache$ = null;
    }

    if (!this.udtViewModelCache$) {
      const url = `${PeriforApiEndpoint}/UndisturbedTemperature/view-model`;
      this.udtViewModelCache$ = this.http.get<UdtViewModel>(url).pipe(
        catchError(error => {
          console.error('Error loading UDT ViewModel:', error);
          return of(null);
        }),
        shareReplay(1)
      );
    }
    // Clear cache after 1 second to allow fresh data on next load
    setTimeout(() => this.udtViewModelCache$ = null, 1000);
    return this.udtViewModelCache$;
  }

  public getUndisturbedTemperature(): Observable<UndisturbedTemperature> {
    if (!this.undisturbedTemperatureCache$) {
      const url = `${PeriforApiEndpoint}/UndisturbedTemperature`;
      this.undisturbedTemperatureCache$ = this.http.get(url).pipe(
        map(data => new UndisturbedTemperature(data)),
        catchError(() => of(null as UndisturbedTemperature)),
        shareReplay(1)
      );
    }
    setTimeout(() => this.undisturbedTemperatureCache$ = null, 1000);
    return this.undisturbedTemperatureCache$;
  }

  updateUndisturbedTemperature(undisturbedTemperature: UndisturbedTemperature) {
    return this.http.put(`${PeriforApiEndpoint}/UndisturbedTemperature`, undisturbedTemperature, httpOptions);
  }

  public getUndisturbedTemperaturePlot(): Observable<UndisturbedTemperaturePlot> {
    if (!this.undisturbedTemperaturePlotCache$) {
      this.undisturbedTemperaturePlotCache$ = this.http
        .get(`${PeriforApiEndpoint}/UndisturbedTemperature/plot`)
        .pipe(
          map(data => new UndisturbedTemperaturePlot(data)),
          catchError(() => of(null as UndisturbedTemperaturePlot)),
          shareReplay(1)
        );
    }
    setTimeout(() => this.undisturbedTemperaturePlotCache$ = null, 1000);
    return this.undisturbedTemperaturePlotCache$;
  }

  getUndisturbedTemperaturePoints(): Observable<CustomPoint[]> {
    if (!this.temperaturePointsCache$) {
      this.temperaturePointsCache$ = this.http.get<CustomPoint[]>(`${PeriforApiEndpoint}/UndisturbedTemperature/points`)
        .pipe(
          map(data => data.map(item => new CustomPoint(item))),
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.temperaturePointsCache$ = null, 1000);
    return this.temperaturePointsCache$;
  }

  /**
   * Generates custom UDT points from the current constant gradient settings.
   * Action endpoint: Resets custom UDT points to values calculated from current gradient settings.
   * Backend saves the generated points to DB and fires SignalR event REFRESH_UDT_INPUTS.
    * Frontend can then force-refresh UDT data immediately and also react to SignalR refresh events.
   * @returns Observable that completes when action succeeds
   */
  generateCustomPointsFromGradient(): Observable<void> {
    return this.http.post<void>(`${PeriforApiEndpoint}/UndisturbedTemperature/convert-to-custom`, null, httpOptions);
  }

  /**
   * Interpolates temperature at a specific depth using the backend UDT calculation engine.
   * @param depth The depth value
   * @param depthType Whether depth is 'tvd' or 'md' (defaults to 'tvd')
   * @returns Observable containing temperature and depths
   */
  public interpolateTemperature(depth: number, depthType: 'tvd' | 'md' = 'tvd'): Observable<InterpolatedTemperatureResult> {
    const url = `${PeriforApiEndpoint}/UndisturbedTemperature/interpolate?depth=${depth}&depthType=${depthType}`;
    return this.http.get<InterpolatedTemperatureResult>(url);
  }
}

/**
 * Result from backend temperature interpolation query.
 */
export interface InterpolatedTemperatureResult {
  temperature: number;
  trueVerticalDepth: number;
  measuredDepth: number;
}
